/**
 * Observability Service
 * 
 * Provides uptime monitoring, error tracking, and performance metrics.
 * Makes I2 (Reliability and observability meet enterprise expectations) true.
 */

// NOTE: getOrCreateSupabaseUser commented out - not needed since audit_events storage is disabled
import { getSupabaseClient } from './supabaseClient';
// import { getOrCreateSupabaseUser } from './userSync';

// ============================================================================
// Types
// ============================================================================

export type MetricType = 
  | 'api_latency'
  | 'page_load'
  | 'tool_execution'
  | 'db_query'
  | 'error_rate'
  | 'availability';

export type MetricSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface MetricEvent {
  id?: string;
  metricType: MetricType;
  name: string;
  value: number;
  unit: string;
  tags?: Record<string, string>;
  severity?: MetricSeverity;
  timestamp: number;
}

export interface ErrorEvent {
  id?: string;
  errorType: string;
  message: string;
  stack?: string;
  context?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp: number;
}

export interface HealthCheckResult {
  service: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  latencyMs: number;
  message?: string;
  checkedAt: number;
}

export interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'unhealthy';
  checks: HealthCheckResult[];
  lastUpdated: number;
}

// ============================================================================
// Metric Recording
// ============================================================================

const metricsBuffer: MetricEvent[] = [];
const BUFFER_FLUSH_INTERVAL = 10000; // 10 seconds
const BUFFER_MAX_SIZE = 50;

/**
 * Record a metric event
 */
export function recordMetric(event: Omit<MetricEvent, 'timestamp'>): void {
  const metric: MetricEvent = {
    ...event,
    timestamp: Date.now(),
  };
  
  metricsBuffer.push(metric);
  
  // Flush if buffer is full
  if (metricsBuffer.length >= BUFFER_MAX_SIZE) {
    flushMetrics();
  }
  
  // Also log to console in development
  if (import.meta.env.DEV) {
    console.debug('[Metric]', metric.metricType, metric.name, metric.value, metric.unit);
  }
}

/**
 * Record API latency
 */
export function recordApiLatency(endpoint: string, latencyMs: number, statusCode?: number): void {
  recordMetric({
    metricType: 'api_latency',
    name: endpoint,
    value: latencyMs,
    unit: 'ms',
    tags: { statusCode: String(statusCode || 200) },
    severity: latencyMs > 2000 ? 'warning' : 'info',
  });
}

/**
 * Record page load time
 */
export function recordPageLoad(pageName: string, loadTimeMs: number): void {
  recordMetric({
    metricType: 'page_load',
    name: pageName,
    value: loadTimeMs,
    unit: 'ms',
    severity: loadTimeMs > 3000 ? 'warning' : 'info',
  });
}

/**
 * Record tool execution time
 */
export function recordToolExecution(
  toolName: string, 
  executionTimeMs: number, 
  itemCount: number,
  success: boolean
): void {
  recordMetric({
    metricType: 'tool_execution',
    name: toolName,
    value: executionTimeMs,
    unit: 'ms',
    tags: { itemCount: String(itemCount), success: String(success) },
    severity: success ? 'info' : 'error',
  });
}

/**
 * Flush metrics buffer to storage
 * Note: This fails silently to avoid impacting the main application
 */
async function flushMetrics(): Promise<void> {
  if (metricsBuffer.length === 0) return;
  
  const toFlush = [...metricsBuffer];
  metricsBuffer.length = 0;
  
  // In production, this would send to a metrics service (DataDog, New Relic, etc.)
  // For now, we just log to console in dev mode
  if (import.meta.env.DEV) {
    console.debug(`[Observability] Flushing ${toFlush.length} metrics (local only - audit_events disabled)`);
  }
  
  // NOTE: Supabase audit_events storage is disabled to prevent CORS/access errors
  // The audit_events table needs proper RLS policies or to be created first
  // Uncomment the below when the table is properly set up:
  /*
  try {
    const supabase = getSupabaseClient();
    await supabase.from('audit_events').insert({
      event_type: 'MetricsBatch',
      payload: {
        metrics: toFlush,
        batchSize: toFlush.length,
        timeRange: {
          start: Math.min(...toFlush.map(m => m.timestamp)),
          end: Math.max(...toFlush.map(m => m.timestamp)),
        },
      },
      created_by: 'system',
    });
  } catch (error) {
    // Fail silently - observability should never break the app
    if (import.meta.env.DEV) {
      console.warn('[Observability] Failed to flush metrics to Supabase:', error);
    }
  }
  */
}

// Auto-flush on interval
if (typeof window !== 'undefined') {
  setInterval(flushMetrics, BUFFER_FLUSH_INTERVAL);
  
  // Flush before page unload
  window.addEventListener('beforeunload', () => {
    flushMetrics();
  });
}

// ============================================================================
// Error Tracking
// ============================================================================

const errorsBuffer: ErrorEvent[] = [];

/**
 * Record an error event
 */
export function recordError(
  error: Error | string,
  context?: Record<string, unknown>
): void {
  const errorEvent: ErrorEvent = {
    errorType: error instanceof Error ? error.name : 'Error',
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    context,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    timestamp: Date.now(),
  };
  
  errorsBuffer.push(errorEvent);
  
  // Log to console
  console.error('[Error Tracked]', errorEvent);
  
  // Immediately flush errors (they're important)
  flushErrors();
}

/**
 * Flush error buffer
 * Note: This fails silently to avoid impacting the main application
 */
async function flushErrors(): Promise<void> {
  if (errorsBuffer.length === 0) return;
  
  const toFlush = [...errorsBuffer];
  errorsBuffer.length = 0;
  
  // Log errors to console only - Supabase storage disabled
  if (import.meta.env.DEV) {
    console.debug(`[Observability] ${toFlush.length} error(s) recorded (local only - audit_events disabled)`);
  }
  
  // NOTE: Supabase audit_events storage is disabled to prevent CORS/access errors
  // Uncomment the below when the table is properly set up:
  /*
  try {
    const supabase = getSupabaseClient();
    
    for (const error of toFlush) {
      await supabase.from('audit_events').insert({
        event_type: 'ErrorOccurred',
        payload: error,
        created_by: error.userId || 'anonymous',
      });
    }
  } catch (err) {
    // Fail silently
    if (import.meta.env.DEV) {
      console.warn('[Observability] Failed to flush errors to Supabase:', err);
    }
  }
  */
}

/**
 * Set up global error handlers
 */
export function setupGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  
  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    recordError(event.error || event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    recordError(
      event.reason instanceof Error ? event.reason : String(event.reason),
      { type: 'unhandledrejection' }
    );
  });
}

// ============================================================================
// Health Checks
// ============================================================================

/**
 * Check Supabase connectivity
 */
async function checkSupabaseHealth(): Promise<HealthCheckResult> {
  const start = Date.now();
  
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('users').select('id').limit(1);
    
    const latency = Date.now() - start;
    
    if (error) {
      return {
        service: 'supabase',
        status: 'unhealthy',
        latencyMs: latency,
        message: error.message,
        checkedAt: Date.now(),
      };
    }
    
    return {
      service: 'supabase',
      status: latency > 1000 ? 'degraded' : 'healthy',
      latencyMs: latency,
      checkedAt: Date.now(),
    };
  } catch (error) {
    return {
      service: 'supabase',
      status: 'unhealthy',
      latencyMs: Date.now() - start,
      message: error instanceof Error ? error.message : 'Unknown error',
      checkedAt: Date.now(),
    };
  }
}

/**
 * Check browser localStorage
 */
function checkLocalStorageHealth(): HealthCheckResult {
  const start = Date.now();
  
  try {
    const testKey = '__health_check__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    
    return {
      service: 'localStorage',
      status: 'healthy',
      latencyMs: Date.now() - start,
      checkedAt: Date.now(),
    };
  } catch (error) {
    return {
      service: 'localStorage',
      status: 'degraded',
      latencyMs: Date.now() - start,
      message: 'localStorage unavailable or quota exceeded',
      checkedAt: Date.now(),
    };
  }
}

/**
 * Run all health checks
 */
export async function runHealthChecks(): Promise<SystemHealth> {
  const checks = await Promise.all([
    checkSupabaseHealth(),
    Promise.resolve(checkLocalStorageHealth()),
  ]);
  
  // Determine overall status
  const hasUnhealthy = checks.some(c => c.status === 'unhealthy');
  const hasDegraded = checks.some(c => c.status === 'degraded');
  
  const overall = hasUnhealthy ? 'unhealthy' : hasDegraded ? 'degraded' : 'healthy';
  
  return {
    overall,
    checks,
    lastUpdated: Date.now(),
  };
}

// ============================================================================
// Performance Monitoring
// ============================================================================

/**
 * Create a performance timer
 */
export function createTimer(_name: string): { stop: () => number } {
  const start = performance.now();
  
  return {
    stop: () => {
      const duration = performance.now() - start;
      return duration;
    },
  };
}

/**
 * Measure and record async operation
 */
export async function measureAsync<T>(
  name: string,
  metricType: MetricType,
  operation: () => Promise<T>
): Promise<T> {
  const timer = createTimer(name);
  
  try {
    const result = await operation();
    const duration = timer.stop();
    
    recordMetric({
      metricType,
      name,
      value: duration,
      unit: 'ms',
      severity: 'info',
    });
    
    return result;
  } catch (error) {
    const duration = timer.stop();
    
    recordMetric({
      metricType,
      name,
      value: duration,
      unit: 'ms',
      severity: 'error',
      tags: { error: 'true' },
    });
    
    throw error;
  }
}

// ============================================================================
// Uptime Tracking
// ============================================================================

let sessionStartTime: number | null = null;

/**
 * Start session uptime tracking
 */
export function startUptimeTracking(): void {
  sessionStartTime = Date.now();
  
  // Record session start
  recordMetric({
    metricType: 'availability',
    name: 'session_start',
    value: 1,
    unit: 'count',
  });
}

/**
 * Get current session uptime in seconds
 */
export function getSessionUptime(): number {
  if (!sessionStartTime) return 0;
  return Math.floor((Date.now() - sessionStartTime) / 1000);
}

/**
 * Record session end
 */
export function endUptimeTracking(): void {
  if (!sessionStartTime) return;
  
  recordMetric({
    metricType: 'availability',
    name: 'session_duration',
    value: getSessionUptime(),
    unit: 'seconds',
  });
  
  flushMetrics();
  sessionStartTime = null;
}

// Auto-start tracking
if (typeof window !== 'undefined') {
  startUptimeTracking();
  window.addEventListener('beforeunload', endUptimeTracking);
}


